import { FC, useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import Button, { ButtonProps } from '../Button';
import { Container } from 'styled/layouts/container';
import { Grid } from 'styled/layouts/grid';
import { TypeBody, TypeHeadingTertiary } from 'components/library/Typography';
import {
  HighlightSection,
  HighlightHeadline,
  HighlightWrapper,
  HighlightGrid,
  HighlightFrame,
  HighlightFrameTitle,
} from './highlight.styled';
import Label from '../Label';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';

export type variants = 'left' | 'right' | 'below-3' | 'below-2' | 'below';

interface HighlightItem {
  media: { src: string; alt: string; aspectRatio?: string; mobileSrc?: string };
  pill?: string;
  title?: string;
}

interface HighlightProps {
  title: string;
  body: string | React.ReactNode;
  primaryButton: ButtonProps;
  items: HighlightItem[];
  variant: variants;
  borderRadius?: string;
  applyTransform?: boolean;
  blockSize?: string;
  label?: React.ReactNode;
}

const Highlight: FC<HighlightProps> = ({
  title,
  body,
  primaryButton,
  variant,
  borderRadius,
  items,
  applyTransform = true,
  blockSize,
  label,
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const isSmall = useClientSideMediaQuery(960);
  const y = useTransform(scrollYProgress, [0, 1.2], ['8%', '-10%']);
  const scale = useTransform(scrollYProgress, [0, 1.2], [1, 1.2]);

  return (
    <HighlightSection borderRadius={borderRadius}>
      <Container>
        <Grid noRowGap>
          {/* Headline */}
          <HighlightHeadline variant={variant}>
            {label ? <Label size='small'>{label}</Label> : null}
            <TypeHeadingTertiary>{title}</TypeHeadingTertiary>
            <TypeBody>{body}</TypeBody>
            {primaryButton && (
              <Button url={primaryButton.url} animated={true}>
                {primaryButton.label}
              </Button>
            )}
          </HighlightHeadline>

          {/* Content */}
          <HighlightWrapper variant={variant}>
            {/* Grid media */}
            <HighlightGrid variant={variant} total={items.length}>
              {items.map((item: HighlightItem, index: number) => {
                const isVideo = item.media.src.includes('mp4');
                return (
                  <HighlightFrame
                    key={index}
                    className='card'
                    ref={ref}
                    blockSize={blockSize}
                  >
                    {isVideo ? (
                      <video
                        muted
                        autoPlay
                        playsInline
                        loop
                        title={item.media.alt}
                      >
                        <source src={item.media.src} type='video/mp4' />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <motion.img
                        src={
                          isSmall && item.media.mobileSrc
                            ? item.media.mobileSrc
                            : item.media.src
                        }
                        alt={item.media.alt}
                        loading='lazy'
                        decoding='async'
                        style={applyTransform ? { y, scale } : {}}
                      />
                    )}
                    {item.title && (
                      <HighlightFrameTitle data-pill={item?.pill}>
                        {item.title}
                      </HighlightFrameTitle>
                    )}
                  </HighlightFrame>
                );
              })}
            </HighlightGrid>
          </HighlightWrapper>
        </Grid>
      </Container>
    </HighlightSection>
  );
};

export default Highlight;
